/**
 * useImageGeneration - Delegates job lifecycle to GenerationContext so that
 * progress + result survive tab switches and page reloads. The returned
 * shape is identical to the pre-context version; ImageGenerationPage
 * needs no changes.
 */

import { useState, useCallback, useEffect } from 'react';
import { imageGenerationAPI } from '../services/imageGenerationAPI';
import { audioNotification } from '../utils/audioNotification';
import sharedConstants from '../constants/shared_constants.json';
import type { ImageGenerationConfig, ImageResult } from '../types';
import { ensureArray } from '../utils/apiNormalizers';
import { DEFAULT_AI_IMAGE_PROVIDER } from '../constants/imageSource';
import { notifyTokenBalanceChanged } from './useTokenBalance';
import * as componentService from '../services/componentService';
import { handleError } from '../utils/errorHandler';
import { apiFailureError, HttpResponseError, type ApiFailureSignals } from '../utils/httpUtils';
import { useGeneration } from '../contexts/GenerationContext.context';
import { useGenerationJob } from './useGenerationJob';
import type { ImageJobResult } from '../services/polling/imagePollAdapter';
import { estimateImageGenerationSeconds, parseResolution } from '../utils/imageTimeEstimator';
import { isElectronMode, isLocalMode } from '../utils/apiMode';

// Get default styles from shared constants (Fooocus defaults)
const rawStyles = sharedConstants.constants?.defaults?.IMAGE_DEFAULT_STYLES;
const IMAGE_DEFAULT_STYLES: string[] = Array.isArray(rawStyles)
  ? rawStyles
  : ['Fooocus V2', 'Fooocus Enhance', 'Fooocus Sharp'];

const DEFAULT_CONFIG: ImageGenerationConfig = {
  prompt: '',
  negative_prompt: '',
  styles: IMAGE_DEFAULT_STYLES,
  resolution: '1152x896',
  num_images: 1,
  seed: -1,
  guidance_scale: 4.0,
  num_steps: 30,
  sharpness: 2.0,
  performance: 'Speed',
  output_format: 'png',
  async_process: true,
  provider: DEFAULT_AI_IMAGE_PROVIDER,
  model: undefined,
};

export interface UseImageGenerationReturn {
  /** Current form configuration */
  config: ImageGenerationConfig;
  /** Update configuration */
  setConfig: React.Dispatch<React.SetStateAction<ImageGenerationConfig>>;
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Smooth client-driven progress (0-100), backed by time estimator. */
  progress: number;
  /** ISO timestamp when the current generation started, or null. */
  startedAt: string | null;
  /** Estimated total wall-clock seconds for the current run. */
  expectedSeconds: number;
  /** True when provider may cold-start (own RunPod worker / local GPU). */
  mayColdStart: boolean;
  /** Generated images result */
  result: { images: ImageResult[]; processingTime: number } | null;
  /** Error message if generation failed */
  error: string | null;
  /**
   * Machine-readable signals for the failure behind `error`, when the request
   * reached the server (`{}` for a network/parse failure, null when there is no
   * failure). The page branches on this to decide WHICH outcome to show —
   * notably an insufficient-tokens refusal, which must offer a purchase rather
   * than render the backend's prose.
   */
  failure: ApiFailureSignals | null;
  /** Start image generation */
  generateImage: () => Promise<void>;
  /** Reset state to initial */
  reset: () => void;
}

export function useImageGeneration(): UseImageGenerationReturn {
  const { startJob, completeJob } = useGeneration();

  const [config, setConfig] = useState<ImageGenerationConfig>(DEFAULT_CONFIG);
  const [startError, setStartError] = useState<string | null>(null);
  const [startFailure, setStartFailure] = useState<ApiFailureSignals | null>(null);
  // Tracks the bloqueante POST /api/v1/image/generate window. Lets <ImageProgress>
  // render while RunPod runsync waits (no backend job_id exists yet, so we can't
  // rely on the GenerationContext active-job flag for this segment).
  const [isStartingSync, setIsStartingSync] = useState(false);

  // Fetch hardware-recommended performance preset on mount.
  // `/api/v1/components/*` is a local-only route (CLAUDE.md RULE #19). In
  // serverless/cloud browser mode there is no local Fooocus install to probe, so
  // the endpoint always 404s — gate the fetch to skip it entirely (mirrors the
  // guard in useComponentStatus).
  useEffect(() => {
    if (!isElectronMode() && !isLocalMode()) return;
    componentService
      .getComponent('fooocus')
      .then((component) => {
        const recommended = (component as unknown as Record<string, unknown>)
          .recommended_performance;
        if (recommended && typeof recommended === 'string') {
          setConfig((prev) => ({
            ...prev,
            performance: recommended as ImageGenerationConfig['performance'],
          }));
        }
      })
      .catch(() => {
        // Fooocus not installed or API unavailable - keep default
      });
  }, []);

  const { width, height } = parseResolution(config.resolution);
  const { expectedSeconds, mayColdStart } = estimateImageGenerationSeconds({
    provider: config.provider,
    numImages: config.num_images,
    numSteps: config.num_steps,
    width,
    height,
    performance: config.performance,
  });

  // isStartingSync flows in as extraActive: it keeps <ImageProgress> rendering
  // during the blocking POST /generate window before a backend job_id exists.
  const { isGenerating, progress, startedAt, result, jobError, clearJobs } =
    useGenerationJob<ImageJobResult>('image', { expectedSeconds, extraActive: isStartingSync });

  const error = startError ?? jobError;

  const generateImage = useCallback(async (): Promise<void> => {
    if (!config.prompt.trim()) {
      setStartError('Please enter a prompt');
      return;
    }

    setStartError(null);
    setStartFailure(null);
    setIsStartingSync(true);

    try {
      const response = await imageGenerationAPI.generateImage(config);

      if (!response.success || !response.data) {
        // apiFailureError, not `new Error(response.error)`: the second erases
        // the status and error.code the response carried, and with them the
        // only way to tell "you are out of tokens" from "that provider is
        // misconfigured". The catch below reads them back off this error.
        throw apiFailureError(response, 'Failed to start image generation');
      }

      const data = response.data;

      // Sync path: RunPod runsync returns completed images directly (no job_id).
      if (data.images && data.images.length > 0) {
        const images = ensureArray(data.images);
        const syntheticId = `img-sync-${Date.now()}`;
        const job = startJob<ImageGenerationConfig>({
          id: syntheticId,
          tabMode: 'image',
          serverType: 'generation',
          metadata: config,
          title: 'Image generation',
        });
        completeJob(job.id, {
          images,
          processingTime: data.processing_time_seconds ?? 0,
        });
        audioNotification.playSuccess().catch(() => {});
        notifyTokenBalanceChanged();
        return;
      }

      // Async path: poll for status via GenerationContext.
      if (data.job_id) {
        startJob<ImageGenerationConfig>({
          id: data.job_id,
          tabMode: 'image',
          serverType: 'generation',
          metadata: config,
          title: 'Image generation',
        });
        notifyTokenBalanceChanged();
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (err) {
      const errorMessage = handleError(err, 'ImageGeneration', 'An error occurred');
      setStartError(errorMessage);
      setStartFailure(
        err instanceof HttpResponseError ? { errorCode: err.code, status: err.status } : {}
      );
      notifyTokenBalanceChanged();
      audioNotification.playError().catch(() => {});
    } finally {
      setIsStartingSync(false);
    }
  }, [config, startJob, completeJob]);

  const reset = useCallback((): void => {
    setStartError(null);
    setStartFailure(null);
    setConfig(DEFAULT_CONFIG);
    clearJobs();
  }, [clearJobs]);

  return {
    config,
    setConfig,
    isGenerating,
    progress,
    startedAt,
    expectedSeconds,
    mayColdStart,
    result,
    error,
    failure: startFailure,
    generateImage,
    reset,
  };
}

export default useImageGeneration;
